import './ManageUserData.scss';
import '../Admin.scss';

import { useQuery, useMutation } from '@tanstack/react-query';
import { useServices } from 'index';
import { Input, Select } from '@trackman/web-shared-components';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { useGetUserManagementCertificates } from 'queryHooks/certificates';

export const ManageUserData = observer(() => {
  useSetPageTitle({ title: 'User' });
  const userDataService = useServices().manageUserDataService;
  const userStore = useStores().manageUserDataStore;
  const notificationStore = useStores().notificationStore;

  const [certificateGrantUserId, setCertificateGrantUserId] = useState('');
  const [selectedCertificateId, setSelectedCertificateId] = useState('');

  const certificates = useGetUserManagementCertificates();
  const grantCertificationTestAccess = useMutation({
    mutationFn: ({ userId, certificationTestId }: { userId: string; certificationTestId: string }) =>
      userDataService.grantCertificationTestAccess(userId, certificationTestId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Certification test access granted', 'success');
    },
    onError: (error: Error) => {
      notificationStore.addToast('error', getErrorMessage(error), 'error');
    },
  });
  const revokeCertificationTestAccess = useMutation({
    mutationFn: ({ userId, certificationTestId }: { userId: string; certificationTestId: string }) =>
      userDataService.revokeCertificationTestAccess(userId, certificationTestId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Certification test access revoked', 'success');
    },
    onError: (error: Error) => {
      notificationStore.addToast('error', getErrorMessage(error), 'error');
    },
  });
  const grantCertificate = useMutation({
    mutationFn: ({ userId, certificateId }: { userId: string; certificateId: string }) =>
      userDataService.grantCertificate(userId, certificateId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Certificate access granted', 'success');
    },
    onError: (error: Error) => {
      notificationStore.addToast('error', getErrorMessage(error), 'error');
    },
  });
  const revokeCertificate = useMutation({
    mutationFn: ({ userId, certificateId }: { userId: string; certificateId: string }) =>
      userDataService.revokeCertificate(userId, certificateId),
    onSuccess: () => {
      notificationStore.addToast('success', 'Certificate access revoked', 'success');
    },
    onError: (error: Error) => {
      notificationStore.addToast('error', getErrorMessage(error), 'error');
    },
  });

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>User</h1>
      </div>
      <div className='admin-content-container manage-user-data-container'>
        <div className='admin-edit-section'>
          <h2>Membership</h2>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={userStore.supportMembershipUserId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.supportMembershipUserId = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.addSupportMembership()}>
                Add Support Membership
              </button>
            </div>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={userStore.supportMembershipUserId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.supportMembershipUserId = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.cancelSupportMembership()}>
                Cancel Support Membership
              </button>
            </div>
          </div>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={userStore.terminateSubscriptionUserId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.terminateSubscriptionUserId = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.terminateSubscription()}>
                Terminate Subscription
              </button>
            </div>
          </div>
        </div>
        <div className='admin-edit-section'>
          <h2>Manage Master role</h2>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={userStore.giveLevelMasterUserId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.giveLevelMasterUserId = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.updatePlayerLevelMaster()}>
                Add Master role
              </button>
            </div>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={userStore.generateCertificatePdfUserId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.generateCertificatePdfUserId = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.generateCertificatePdf()}>
                Generate Master Certificate
              </button>
            </div>
          </div>
        </div>
        <div className='admin-edit-section'>
          <h2>Grant certification access</h2>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={certificateGrantUserId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCertificateGrantUserId(e.currentTarget.value)}
              />
              <Select
                label='Certificate'
                value={selectedCertificateId}
                onChange={(e) => setSelectedCertificateId(e.target.value as string)}
                selectOptions={[
                  { label: 'Select certificate', value: '' },
                  ...(certificates.data ?? []).map((c) => ({ label: c.title, value: c.id })),
                ]}
              />
            </div>
            <div className='input-wrapper'>
              <button
                className='styled-button secondary'
                disabled={!certificateGrantUserId || !selectedCertificateId || grantCertificationTestAccess.isPending}
                onClick={() =>
                  certificateGrantUserId &&
                  selectedCertificateId &&
                  grantCertificationTestAccess.mutate({
                    userId: certificateGrantUserId,
                    certificationTestId: selectedCertificateId,
                  })
                }
              >
                Grant cert test access
              </button>
              <button
                className='styled-button secondary'
                disabled={!certificateGrantUserId || !selectedCertificateId || revokeCertificationTestAccess.isPending}
                onClick={() =>
                  certificateGrantUserId &&
                  selectedCertificateId &&
                  revokeCertificationTestAccess.mutate({
                    userId: certificateGrantUserId,
                    certificationTestId: selectedCertificateId,
                  })
                }
              >
                Revoke cert test access
              </button>
            </div>
            <div className='input-wrapper'>
              <button
                className='styled-button secondary'
                disabled={!certificateGrantUserId || !selectedCertificateId || grantCertificate.isPending}
                onClick={() =>
                  certificateGrantUserId &&
                  selectedCertificateId &&
                  grantCertificate.mutate({
                    userId: certificateGrantUserId,
                    certificateId: selectedCertificateId,
                  })
                }
              >
                Grant certificate
              </button>
              <button
                className='styled-button secondary'
                disabled={!certificateGrantUserId || !selectedCertificateId || revokeCertificate.isPending}
                onClick={() =>
                  certificateGrantUserId &&
                  selectedCertificateId &&
                  revokeCertificate.mutate({
                    userId: certificateGrantUserId,
                    certificateId: selectedCertificateId,
                  })
                }
              >
                Revoke certificate
              </button>
            </div>
          </div>
        </div>
        <div className='admin-edit-section'>
          <h2>User data</h2>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input
                label='User email'
                value={userStore.userSearchEmail ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.userSearchEmail = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.getUserIdByEmail()}>
                Get user ID by email
              </button>
              {userStore.foundUserId ? <p className='found-data'>User ID: {userStore.foundUserId}</p> : null}
            </div>
          </div>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input
                label='User ID'
                value={userStore.userSearchId ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => (userStore.userSearchId = e.currentTarget.value)}
              />
              <button className='styled-button secondary' onClick={() => userStore.getUserEmailById()}>
                Get user email by ID
              </button>
              {userStore.foundUserEmail ? <p className='found-data'>User email: {userStore.foundUserEmail}</p> : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default ManageUserData;
